<template>
  <div class="basic-canvas">
    <div class="canvas-header">
      <h3>Canvas with Drag & Drop</h3>
      <div class="header-controls">
        <el-button size="mini" @click="addItem" type="primary">Add Item</el-button>
        <el-button size="mini" @click="toggleMode">
          {{ isCanvasMode ? 'Grid Mode' : 'Canvas Mode' }}
        </el-button>
      </div>
    </div>
    
    <div class="canvas-area" :class="{ 'canvas-mode': isCanvasMode }">
      <!-- Canvas Mode: Free positioning -->
      <div v-if="isCanvasMode" class="canvas-container" ref="canvasContainer">
        <div 
          v-for="item in items" 
          :key="item.id"
          class="draggable-card"
          :class="{ selected: selectedItems.includes(item.id) }"
          :style="{ 
            left: item.x + 'px', 
            top: item.y + 'px',
            width: item.width + 'px'
          }"
          @mousedown="startDrag($event, item)"
          @click="selectItem(item)"
        >
          <div class="card-content">
            <h4>{{ item.title }}</h4>
            <p>{{ item.content }}</p>
            <div class="card-actions">
              <el-button size="mini" @click.stop="deleteItem(item)" type="danger">Delete</el-button>
            </div>
          </div>
        </div>
        
        <div v-if="items.length === 0" class="empty-canvas">
          <p>Canvas Mode: Items can be dragged around freely</p>
          <p>Click "Add Item" to start</p>
        </div>
      </div>
      
      <!-- Grid Mode: Grid layout -->
      <div v-else class="canvas-grid">
        <div 
          v-for="item in items" 
          :key="item.id"
          class="canvas-card"
          @click="selectItem(item)"
        >
          <h4>{{ item.title }}</h4>
          <p>{{ item.content }}</p>
          <el-button size="mini" @click.stop="deleteItem(item)" type="danger">Delete</el-button>
        </div>
        
        <div v-if="items.length === 0" class="empty-state">
          <p>Grid Mode: Items arranged in a grid</p>
          <p>Click "Add Item" to start</p>
        </div>
      </div>
    </div>
    
    <div class="canvas-footer">
      <span>Items: {{ items.length }}</span>
      <span v-if="selectedItems.length">Selected: {{ selectedItems.length }}</span>
      <span>Mode: {{ isCanvasMode ? 'Canvas' : 'Grid' }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BasicWorkflowCanvas',
  props: {
    inputData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      items: [],
      selectedItems: [],
      isCanvasMode: false,
      isDragging: false,
      dragItem: null,
      dragOffset: { x: 0, y: 0 },
      cardWidth: 250,
      cardHeight: 150
    };
  },
  mounted() {
    console.log('BasicCanvas: mounted with inputData:', this.inputData);
    this.loadData();
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  },
  watch: {
    inputData: {
      handler(newData, oldData) {
        console.log('BasicCanvas: inputData changed', { newData, oldData });
        // Always reload when data source changes
        this.loadData();
      },
      deep: false,
      immediate: true
    }
  },
  methods: {
    loadData() {
      console.log('BasicCanvas: loadData called with inputData:', this.inputData);
      
      if (this.inputData && this.inputData.length > 0) {
        // Preserve existing positions if items already exist
        const existingPositions = {};
        this.items.forEach(item => {
          existingPositions[item.id] = { x: item.x, y: item.y };
        });
        
        this.items = this.inputData.map((item, index) => {
          const itemId = item.id || index + 1;
          const existingPos = existingPositions[itemId];
          
          return {
            id: itemId,
            title: item.title || 'No title',
            content: item.content || 'No content',
            x: existingPos ? existingPos.x : (index % 4) * 280 + 50,
            y: existingPos ? existingPos.y : Math.floor(index / 4) * 180 + 50,
            width: this.cardWidth,
            height: this.cardHeight
          };
        });
        
        console.log('BasicCanvas: items loaded:', this.items);
      } else {
        this.items = [];
        console.log('BasicCanvas: no inputData, items cleared');
      }
    },
    
    addItem() {
      const newItem = {
        id: Date.now(),
        title: `Item ${this.items.length + 1}`,
        content: 'This is a test item',
        x: Math.random() * 400 + 50,
        y: Math.random() * 300 + 50,
        width: this.cardWidth,
        height: this.cardHeight
      };
      this.items.push(newItem);
      this.$message.success('Item added!');
    },
    
    toggleMode() {
      this.isCanvasMode = !this.isCanvasMode;
      if (this.isCanvasMode) {
        this.ensureCanvasPositions();
      }
    },
    
    ensureCanvasPositions() {
      this.items.forEach((item, index) => {
        if (item.x === undefined || item.y === undefined) {
          item.x = (index % 4) * 280 + 50;
          item.y = Math.floor(index / 4) * 180 + 50;
          item.width = this.cardWidth;
          item.height = this.cardHeight;
        }
      });
    },
    
    selectItem(item) {
      if (!this.selectedItems.includes(item.id)) {
        this.selectedItems = [item.id];
      }
      this.$message.info(`Selected: ${item.title}`);
      this.$emit('card-click', item);
    },
    
    startDrag(event, item) {
      if (!this.isCanvasMode) return;
      
      event.preventDefault();
      this.isDragging = true;
      this.dragItem = item;
      
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      this.dragOffset = {
        x: event.clientX - rect.left - item.x,
        y: event.clientY - rect.top - item.y
      };
      
      this.selectItem(item);
    },
    
    handleMouseMove(event) {
      if (!this.isDragging || !this.dragItem || !this.$refs.canvasContainer) return;
      
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      const newX = event.clientX - rect.left - this.dragOffset.x;
      const newY = event.clientY - rect.top - this.dragOffset.y;
      
      // Update position with bounds checking
      this.dragItem.x = Math.max(0, Math.min(newX, rect.width - this.dragItem.width - 32));
      this.dragItem.y = Math.max(0, Math.min(newY, rect.height - this.dragItem.height - 32));
      
      // Force Vue to update the position
      this.$forceUpdate();
    },
    
    handleMouseUp() {
      if (this.isDragging && this.dragItem) {
        console.log('Drag ended, saving position:', this.dragItem.x, this.dragItem.y);
        
        // Ensure the position is saved in the items array
        const itemIndex = this.items.findIndex(item => item.id === this.dragItem.id);
        if (itemIndex !== -1) {
          this.$set(this.items, itemIndex, {
            ...this.items[itemIndex],
            x: this.dragItem.x,
            y: this.dragItem.y
          });
        }
        
        this.isDragging = false;
        this.dragItem = null;
        
        // Emit the updated items
        this.$emit('items-updated', this.items);
        
        // Force update to ensure Vue renders the new position
        this.$forceUpdate();
      }
    },
    
    deleteItem(item) {
      const index = this.items.findIndex(i => i.id === item.id);
      if (index !== -1) {
        this.items.splice(index, 1);
        this.selectedItems = this.selectedItems.filter(id => id !== item.id);
        this.$message.success('Item deleted!');
        this.$emit('items-updated', this.items);
      }
    }
  }
};
</script>

<style scoped>
.basic-canvas {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.canvas-header {
  padding: 16px;
  background: white;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.canvas-header h3 {
  margin: 0;
  color: #303133;
}

.header-controls {
  display: flex;
  gap: 8px;
}

.canvas-area {
  flex: 1;
  overflow: hidden;
}

.canvas-area.canvas-mode {
  background: 
    linear-gradient(45deg, #f0f2f5 25%, transparent 25%),
    linear-gradient(-45deg, #f0f2f5 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f0f2f5 75%),
    linear-gradient(-45deg, transparent 75%, #f0f2f5 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
}

/* Grid Mode */
.canvas-grid {
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
  height: 100%;
  overflow-y: auto;
}

.canvas-card {
  background: white;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s;
}

.canvas-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.canvas-card h4 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 14px;
}

.canvas-card p {
  margin: 0 0 12px 0;
  color: #606266;
  font-size: 13px;
}

/* Canvas Mode */
.canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 16px;
}

.draggable-card {
  position: absolute;
  cursor: move;
  user-select: none;
  transition: box-shadow 0.3s;
}

.draggable-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.draggable-card.selected {
  box-shadow: 0 0 0 2px #409eff;
}

.card-content {
  background: white;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.card-content h4 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 14px;
}

.card-content p {
  margin: 0 0 12px 0;
  color: #606266;
  font-size: 13px;
  flex: 1;
  overflow: hidden;
}

.card-actions {
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

/* Empty states */
.empty-state, .empty-canvas {
  text-align: center;
  padding: 48px;
  color: #909399;
}

.empty-state {
  grid-column: 1 / -1;
}

.empty-canvas {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
}

.canvas-footer {
  padding: 12px 16px;
  background: white;
  border-top: 1px solid #ebeef5;
  font-size: 12px;
  color: #606266;
  display: flex;
  gap: 16px;
}
</style>